import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { Colors } from './theme';
import { AlertHost, ModalPortalContext } from './web/patchedReactNative';

/**
 * Web counterpart to AppShell.tsx. The web app owns the full browser
 * viewport at every breakpoint; individual screens remain responsible for
 * any readable content-column max widths they need.
 *
 * Also owns the Modal portal target (see web/patchedReactNative.tsx) — a
 * plain DOM node living inside the app viewport, so every
 * `<Modal>` in the app (React Native's own component, patched at the
 * `react-native` import for the whole web bundle) renders confined to this
 * shell instead of react-native-web's real Modal, which portals to a
 * `position: fixed` node near document.body — structurally outside any
 * amount of CSS applied here.
 */
export const AppShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [portalNode, setPortalNode] = useState<HTMLDivElement | null>(null);

  return (
    <View style={styles.browserViewport}>
      <View style={styles.appViewport}>
        <ModalPortalContext.Provider value={portalNode}>{children}</ModalPortalContext.Provider>
        {/* Sits above `children` in DOM order (paints on top) purely via
            normal stacking — pointerEvents 'none' so it never blocks touches
            when no Modal is currently portaled into it; PatchedModal's own
            rendered content explicitly sets pointerEvents back to 'auto'. */}
        <div ref={setPortalNode} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
        {/* Alert.alert is a no-op in react-native-web (see
            web/patchedReactNative.tsx) — AlertHost is the real dialog UI for
            it, mounted once here so it works from any screen. */}
        <AlertHost />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  browserViewport: {
    // @ts-expect-error web-only CSS values, harmless on native (unused there)
    minHeight: '100dvh',
    // @ts-expect-error web-only CSS values, harmless on native (unused there)
    height: '100dvh',
    width: '100%',
    overflow: 'hidden',
    backgroundColor: '#05070c',
  },
  appViewport: {
    flex: 1,
    width: '100%',
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: Colors.bg,
  },
});
